import * as React from 'react';
import { useCallback, useRef, useState, useLayoutEffect } from 'react';
// import { smootScrollAllowed } from '../../../utils/prefers-reduced-motion.ts';
import { classList } from '../../../utils/string.ts';
import composeRefs from '../../../utils/compose-refs.ts';
import { Icon } from '../../00-atoms/icon/icon.tsx';

export type HiddenScrollbarOptions = React.PropsWithChildren<{
  className?: string;
  arrowsClassName?: string;
  arrowIconsClassName?: string;
  containerClassName?: string;
  /**
   * If the component should directly render the arrows or only return them.
   */
  renderArrows: boolean;
  /**
   * Scroll range, which should be covered, when clicking on next/previous arrow.
   * Default set to 'slideWise'.
   */
  scrollRange?: 'slideWise' | 'elementWise';
  /**
   * If the component should directly render the dots, only return them, or not generate any
   * pagination dots at all.
   * Default set to 'none'.
   */
  renderDots?: 'none' | 'render' | 'generate-only';
  /**
   * If clicking on the next/previous arrows on the last/first slide should result in starting
   * over from the first/last slide again.
   */
  scrollRotation: boolean;
  /**
   * The type of element to render the slides in.
   * Default set to 'list'.
   */
  containerType?: 'list' | 'div';
  /**
   * If not set, initial shown slide is the first one.
   */
  startAtSlide?: number;
}>;

/**
 * Hides the scrollbars and provides clickable arrows as alternatives.
 * Currently only horizontal scrolling is supported.
 * All elements inside the slider container must be fully rendered
 * before the scrolling position can be correctly calculated.
 */
export const useHiddenScrollbar = ({
  className,
  arrowsClassName,
  arrowIconsClassName,
  containerClassName,
  renderArrows,
  scrollRange = 'slideWise',
  renderDots = 'none',
  children,
  scrollRotation,
  containerType = 'list',
  startAtSlide = 0
}: HiddenScrollbarOptions) => {
  const containerRef = useRef<HTMLElement>(null);

  const onContainerRef = (ref: HTMLElement | null) => {
    if (ref) {
      updateControlsVisibility(ref);
    }
  };

  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const [slidesCount, setSlidesCount] = useState(0);
  const [currentSlide, setCurrentSlide] = useState(startAtSlide);

  const updateControlsVisibility = (containerRef: HTMLElement) => {
    setShowLeftArrow(containerRef.scrollLeft > 0);
    setShowRightArrow(
      Math.ceil(containerRef.scrollLeft) < containerRef.scrollWidth - containerRef.clientWidth
    );

    const newSlidesCount = Math.ceil(containerRef.scrollWidth / containerRef.clientWidth);
    setSlidesCount(newSlidesCount);
    const currentSlideIndex = Math.round(
      (containerRef.scrollLeft / containerRef.scrollWidth) * newSlidesCount
    );
    setCurrentSlide(currentSlideIndex);
  };

  const getIndexOfFirstVisibleElement = (slider: HTMLElement) =>
    Array.from(slider.children).findIndex(
      (child: HTMLElement) => child.offsetLeft > slider.scrollLeft
    );

  useLayoutEffect(() => {
    if (containerRef.current) {
      updateControlsVisibility(containerRef.current);
    }
  });

  useLayoutEffect(() => {
    containerRef.current?.scrollTo({
      behavior: 'auto',
      left: containerRef.current.clientWidth * startAtSlide
    });
  }, [startAtSlide]);

  /**
   * In specific cases, elements inside the hiddenScrollbar can be dismissed by the user (e.g. CM plate slider).
   * When this happens, this function can be used to
   *
   * 1. Scroll to the next slide
   * 2. Remove the dismissed element, while fixing the scroll position.
   */
  const closeSlide = (): {
    /**
     * This promise resolves when the scroll animation is done.
     * It should be awaited, before removing the dismissed element from the DOM.
     */
    scrollPromise: Promise<void>;
    /**
     * Should be called in the same moment that the dismissed element is removed from the DOM.
     * The scroll position is then fixed.
     */
    onItemRemoved: () => void;
  } => {
    scrollToNextSlide();

    return {
      scrollPromise: new Promise(resolve => window.setTimeout(() => resolve(), 500)),
      onItemRemoved: () => {
        containerRef.current!.scrollLeft -= containerRef.current!.clientWidth;
      }
    };
  };

  const scrollTo = (destination: number) => {
    containerRef.current?.scrollTo({ behavior: 'smooth', left: destination });
  };

  const scrollToSlide = useCallback(
    (slideIndex: number) =>
      containerRef.current && scrollTo(containerRef.current.clientWidth * slideIndex),
    []
  );

  const scrollToNextElement = useCallback(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }
    const isOnLastSlide = currentSlide === slidesCount - 1;
    scrollTo(
      isOnLastSlide
        ? 0
        : container.scrollLeft +
            container.children.item(getIndexOfFirstVisibleElement(container))!.clientWidth
    );
  }, [currentSlide, slidesCount]);

  const scrollToPreviousElement = useCallback(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }
    const scrolledToFirstElement = container.scrollLeft === 0;
    scrollTo(
      scrolledToFirstElement
        ? container.scrollWidth
        : container.scrollLeft -
            container.children.item(getIndexOfFirstVisibleElement(container) - 1)!.clientWidth
    );
  }, []);

  const scrollToNextSlide = useCallback(() => {
    const isOnLastSlide = currentSlide === slidesCount - 1;
    if (scrollRotation && isOnLastSlide) {
      scrollToSlide(0);
    } else if (containerRef.current) {
      scrollTo(containerRef.current.scrollLeft + containerRef.current.clientWidth);
    }
  }, [scrollRotation, currentSlide, slidesCount, scrollToSlide]);

  const scrollToPreviousSlide = useCallback(() => {
    const isOnFirstSlide = currentSlide === 0;
    if (scrollRotation && isOnFirstSlide) {
      scrollToSlide(slidesCount - 1);
    } else if (containerRef.current) {
      scrollTo(containerRef.current.scrollLeft - containerRef.current.clientWidth);
    }
  }, [scrollRotation, currentSlide, slidesCount, scrollToSlide]);

  const leftArrow = (
    <button
      className={classList(
        'bg-sk-grey-0/75 hover:bg-sk-grey-0 absolute top-1/2 left-0 z-10 block -translate-y-1/2 rounded-r-xl p-4 text-2xl',
        arrowsClassName
      )}
      tabIndex={-1}
      onClick={scrollRange === 'slideWise' ? scrollToPreviousSlide : scrollToPreviousElement}
      disabled={!scrollRotation && !showLeftArrow}
    >
      <Icon name="arrow-left" className={arrowIconsClassName} />
    </button>
  );

  const rightArrow = (
    <button
      className={classList(
        'bg-sk-grey-0/75 hover:bg-sk-grey-0 absolute top-1/2 right-0 z-10 block -translate-y-1/2 rounded-l-xl p-4 text-2xl',
        arrowsClassName
      )}
      tabIndex={-1}
      onClick={scrollRange === 'slideWise' ? scrollToNextSlide : scrollToNextElement}
      disabled={!scrollRotation && !showRightArrow}
    >
      <Icon name="arrow-right" className={arrowIconsClassName} />
    </button>
  );

  const dots = renderDots !== 'none' && (
    <>
      {Array.from({ length: slidesCount }).map((_, index) => (
        <button
          key={index}
          aria-label={`Seite ${index + 1}`}
          className={classList('HiddenScrollbar-dot', currentSlide === index && 'is-active')}
          onClick={() => scrollToSlide(index)}
        />
      ))}
    </>
  );

  const mainElement = (
    <div>
      <div className={classList(containerClassName, 'relative')}>
        {renderArrows && (showLeftArrow || scrollRotation) && leftArrow}

        {/* This is the actual scrolling container. The arrows are not part of it, as they should remain in their position. */}
        {containerType === 'list' && (
          <ul
            className={classList('', className)}
            onScroll={() => containerRef.current && updateControlsVisibility(containerRef.current)}
            ref={composeRefs(containerRef, onContainerRef) as React.RefObject<HTMLUListElement>}
          >
            {children}
          </ul>
        )}
        {containerType === 'div' && (
          <div
            className={classList('', className)}
            onScroll={() => containerRef.current && updateControlsVisibility(containerRef.current)}
            ref={composeRefs(containerRef, onContainerRef) as React.RefObject<HTMLDivElement>}
          >
            {children}
          </div>
        )}

        {renderArrows && (showRightArrow || scrollRotation) && rightArrow}
      </div>
      {renderDots === 'render' && dots}
    </div>
  );

  return {
    mainElement,
    leftArrow: renderArrows ? undefined : leftArrow,
    showLeftArrow,
    rightArrow: renderArrows ? undefined : rightArrow,
    showRightArrow,
    dots: renderDots === 'generate-only' ? dots : undefined,
    slidesCount,
    closeSlide
  };
};
